import { userErrorMessage } from '@wx-weiqi/contracts';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { EbookClient } from '../services/ebooks';
import { nextRequestId } from '../services/http';

export function EbookDeleteDialog({
  client,
  item,
  onClose,
  onDeleted,
}: {
  client: EbookClient;
  item: Awaited<ReturnType<EbookClient['list']>>[number];
  onClose: () => void;
  onDeleted: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const cancel = useRef<HTMLButtonElement>(null);
  const requestId = useRef(nextRequestId('ebook-delete'));
  const submitting = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    const element = dialog.current;
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    element?.showModal();
    cancel.current?.focus();
    document.body.style.overflow = 'hidden';
    return () => {
      element?.close();
      document.body.style.overflow = overflow;
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
      else document.querySelector<HTMLElement>('.ebook-shelf-search input')?.focus();
    };
  }, []);
  async function remove() {
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError('');
    try {
      await client.deleteDocument(item.id, item.stateVersion, requestId.current);
      onDeleted();
    } catch (e) {
      const message = userErrorMessage(e, '删除失败，请稍后重试。');
      setError(
        message.includes('教材已更新')
          ? '教材刚刚有更新。请取消删除，刷新书架后重新确认。'
          : message,
      );
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }
  return createPortal(
    <dialog
      ref={dialog}
      className="ebook-teacher ebook-delete-dialog"
      aria-labelledby="ebook-delete-title"
      aria-describedby="ebook-delete-description"
      aria-busy={busy}
      onCancel={(event) => {
        event.preventDefault();
        if (!submitting.current) onClose();
      }}
    >
      <h2 id="ebook-delete-title">删除这份教材？</h2>
      <p className="ebook-delete-name">{item.name}</p>
      <div id="ebook-delete-description">
        <p>将永久删除这份教材的全部数据：</p>
        <ul>
          <li>原 PDF、封面和页面识别数据</li>
          <li>题目、答案、草稿及修订历史</li>
          <li>已发布电子书、学生作答和学习记录</li>
        </ul>
        <p>学生将无法继续打开这本书。删除后无法恢复。</p>
      </div>
      {error && (
        <p role="alert" className="ebook-error">
          {error}
        </p>
      )}
      <div className="ebook-actions">
        <button ref={cancel} disabled={busy} onClick={onClose}>
          取消
        </button>
        <button className="ebook-delete-confirm" disabled={busy} onClick={() => void remove()}>
          {busy ? '正在删除…' : '确认删除'}
        </button>
      </div>
    </dialog>,
    document.body,
  );
}
